import React, { useCallback, useEffect, useState } from 'react';
import { Eye, RefreshCw } from 'lucide-react';
import {
  Messages, Table, XERO_PULL_PATH, asItems, capabilitiesAllow, errorMessage, formatLabel, formatWhen,
  packPrice, perUnit, scopeKey, statusLabel, useSpRuntime, useSpScope, useStableIds,
} from './SupplierPriceListsShared';
import { BatchReview, ImportForm, SourceForm, SourcePermission } from './SupplierPriceListsForms';
import './Marketplace.css';

const enc = encodeURIComponent;
const DENIED = 'Supplier price lists are not enabled for this organization, or you do not have access.';
const status = e => e?.response?.status;
const detailOf = e => e?.response?.data?.detail;

export default function SupplierPriceLists() {
  const scope = useSpScope();
  return <SupplierPriceListsWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function SupplierPriceListsWorkspace({ scope }) {
  const { active, request, run, busy, error, notice, setError, setNotice } = useSpRuntime(scope);
  const { idFor, release } = useStableIds();
  const [caps, setCaps] = useState(null);
  const [blocked, setBlocked] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('batches');
  const [vendors, setVendors] = useState([]);
  const [sources, setSources] = useState([]);
  const [batches, setBatches] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [detail, setDetail] = useState(null);
  const [current, setCurrent] = useState({});
  const [ingredients, setIngredients] = useState([]);
  const [fileErrors, setFileErrors] = useState(null);
  const canWrite = Boolean(caps?.can_write);

  const deny = useCallback(e => {
    if ([401, 403].includes(status(e))) { setCaps(null); setBlocked(errorMessage(e) || DENIED); return true; }
    return false;
  }, []);
  const fail = useCallback(e => { if (active() && !deny(e)) setError(errorMessage(e)); }, [active, deny, setError]);
  const vendorName = useCallback(id => vendors.find(v => v.id === id)?.name || id || 'Unknown supplier', [vendors]);

  useEffect(() => {
    (async () => {
      try {
        if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to use supplier price lists.');
        if (!scope.locationId) throw new Error('Select a site: supplier price lists are kept per site.');
        const c = await request('get', '/capabilities');
        if (!active()) return;
        if (!capabilitiesAllow(c)) { setBlocked(DENIED); return; }
        setCaps(c);
      } catch (e) {
        if (active()) setBlocked(errorMessage(e));
      } finally {
        if (active()) setLoading(false);
      }
    })();
  }, [scope, request, active]);

  const loadSources = useCallback(async () => {
    try { const data = await request('get', '/sources'); if (active()) setSources(asItems(data)); } catch (e) { fail(e); }
  }, [request, active, fail]);
  const loadBatches = useCallback(async () => {
    try { const data = await request('get', '/imports'); if (active()) setBatches(asItems(data)); } catch (e) { fail(e); }
  }, [request, active, fail]);
  const loadUnmapped = useCallback(async () => {
    try { const data = await request('get', '/offers/unmapped'); if (active()) setUnmapped(asItems(data)); } catch (e) { fail(e); }
  }, [request, active, fail]);

  useEffect(() => {
    if (!caps) return;
    loadSources();
    request('get', '/api/vendors', { external: true })
      .then(data => { if (active()) setVendors(asItems(data)); })
      .catch(() => {}); // names are a nicety; ids still render
  }, [caps, loadSources, request, active]);
  useEffect(() => {
    if (!caps) return;
    if (tab === 'batches') loadBatches();
    if (tab === 'unmapped') loadUnmapped();
  }, [caps, tab, loadBatches, loadUnmapped]);

  const openBatch = useCallback(async batchId => {
    setDetail(null); setCurrent({}); setTab('batches');
    try {
      const data = await request('get', `/imports/${enc(batchId)}`);
      if (!active() || !data?.batch) return;
      setDetail(data);
      const batch = data.batch;
      if (batch.status !== 'open') return;
      const pending = Object.values(batch.pending_confirmations || {});
      const [offers, ings] = await Promise.all([
        request('get', '/offers', { params: { supplier_id: batch.supplier_id, limit: 500 } }).catch(() => null),
        canWrite && pending.includes('ambiguous_mapping')
          ? request('get', '/api/costing/ingredients', { external: true }).catch(() => null) : null,
      ]);
      if (!active()) return;
      setCurrent(Object.fromEntries(asItems(offers).map(o => [o.supplier_sku, o])));
      if (ings) setIngredients(asItems(ings));
    } catch (e) { fail(e); }
  }, [request, active, fail, canWrite]);

  const setPermission = (sourceId, body) => run(async () => {
    await request('post', `/sources/${encodeURIComponent(sourceId)}/permission`, { body }); await loadSources();
  }, body.state === 'approved' ? 'Source approved: it can now import price files.' : 'Source revoked: it can no longer import.');

  const createSource = body => run(async () => { await request('post', '/sources', { body }); await loadSources(); },
    'Source added. It can import once its permission is approved.');

  const upload = async form => {
    setFileErrors(null);
    const res = await run(() => request('post', '/imports', { body: form }));
    if (!res || !active()) return;
    if (res.ok) { setNotice('File staged for review. Nothing is published until a manager approves it.'); await loadBatches(); openBatch(res.result.batch_id); return; }
    const d = detailOf(res.error);
    if (status(res.error) === 422 && d?.blocked) { setFileErrors(d); setError('The file has errors, so nothing was staged.'); } else deny(res.error);
  };

  const pullXero = async () => {
    const res = await run(() => request('post', XERO_PULL_PATH, { body: {} }));
    if (!res || !active()) return;
    if (!res.ok) {
      if (status(res.error) === 404) setError('Pulling from Xero is not available on this server yet.');
      else if (status(res.error) === 409) setError(errorMessage(res.error) || 'Xero is not connected for this site.');
      else deny(res.error);
      return;
    }
    const batchId = res.result?.batch_id || res.result?.batch?.batch_id;
    setNotice(batchId ? 'Xero bills staged for review.' : 'Xero pull finished; no new bills to review.');
    await loadBatches();
    if (batchId) openBatch(batchId);
  };

  const decide = async (batchId, action, payload) => {
    const fingerprint = ['approve', batchId];
    // The key is stable per batch until success, so a retried click replays rather than conflicts.
    const res = await run(() => request('post', `/imports/${enc(batchId)}/${action}`, {
      body: action === 'approve' ? { ...payload, idempotency_key: idFor(fingerprint) } : undefined,
    }),
      action === 'approve' ? 'Approved. These prices are now published for this site.' : 'Batch rejected. Nothing was published.');
    if (!res || !active()) return;
    if (!res.ok) {
      const unresolved = detailOf(res.error)?.unresolved;
      if (unresolved) setError(`Still needs review: ${Object.keys(unresolved).join(', ')}`);
      else deny(res.error);
      return;
    }
    if (action === 'approve') release(fingerprint);
    await loadBatches();
    openBatch(batchId);
  };

  const tabs = [['batches', 'Batches'], ['sources', 'Sources'], ...(canWrite ? [['import', 'Import']] : []), ['unmapped', 'Unmapped']];
  return (
    <main className="mkt-ops" data-testid="supplier-price-lists">
      <header>
        <h1>Supplier price lists</h1>
        {caps && <button type="button" title="Refresh" aria-label="Refresh supplier price lists" disabled={busy}
          onClick={() => { loadSources(); if (tab === 'batches') loadBatches(); if (tab === 'unmapped') loadUnmapped(); }}><RefreshCw size={18} /></button>}
      </header>
      {loading && <p role="status">Checking access...</p>}
      {!loading && !caps && blocked && <div className="mkt-error" role="alert">{blocked}</div>}
      {caps && <>
        <Messages error={error} notice={notice} />
        <div className="mkt-tabs" role="tablist" aria-label="Supplier price list views">
          {tabs.map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} disabled={busy}
            onClick={() => { setTab(key); setDetail(null); setError(''); setNotice(''); }}>{label}</button>)}
        </div>

        {tab === 'sources' && <>
          {canWrite && <SourceForm vendors={vendors} busy={busy} onCreate={createSource} />}
          <Table headers={['Supplier', 'Account reference', 'Format', 'Cadence', 'Permission', 'Can import', ...(caps.can_approve_sources ? [''] : [])]} empty={!sources.length} emptyText="No price-list sources at this site yet." label="Sources">
            {sources.map(s => <tr key={s.source_id}><td>{vendorName(s.supplier_id)}</td><td>{s.supplier_account_reference}</td><td>{formatLabel(s.format)}</td><td>{s.cadence}</td><td title={s.permission_evidence || ''}>{statusLabel(s.permission_state)}</td><td>{s.can_publish ? 'Yes' : 'Not yet'}</td>
              {caps.can_approve_sources && <td><SourcePermission source={s} busy={busy} onSubmit={setPermission} /></td>}</tr>)}
          </Table>
        </>}

        {tab === 'import' && canWrite && <ImportForm key={sources.length} sources={sources} vendorName={vendorName} busy={busy}
          onUpload={upload} onXeroPull={pullXero} blocked={fileErrors} />}

        {tab === 'batches' && <>
          <Table headers={['Staged', 'Source', 'Supplier', 'Snapshot', 'Rows', 'To review', 'Status', 'Approved', '']} empty={!batches.length} emptyText="No imports at this site yet." label="Batches">
            {batches.map(b => (
              <tr key={b.batch_id}>
                <td>{formatWhen(b.created_at)}</td>
                <td>{formatLabel(b.source_format)}</td>
                <td>{vendorName(b.supplier_id)}</td>
                <td>{String(b.snapshot_type || 'delta').toUpperCase()}</td>
                <td className="mkt-number">{b.row_count ?? '—'}</td>
                <td className="mkt-number">{b.status === 'open' ? Object.keys(b.pending_confirmations || {}).length : '—'}</td>
                <td>{statusLabel(b.status)}</td>
                <td>{b.published_at ? `${formatWhen(b.published_at)}${b.published_by ? ` by ${b.published_by}` : ''}` : '—'}</td>
                <td><button type="button" aria-label={`Open batch ${b.batch_id}`} disabled={busy} onClick={() => openBatch(b.batch_id)}><Eye size={16} />Open</button></td>
              </tr>
            ))}
          </Table>
          {detail && <BatchReview key={`${detail.batch.batch_id}-${detail.batch.status}`} detail={detail} current={current} ingredients={ingredients}
            canWrite={canWrite} busy={busy} vendorName={vendorName}
            onApprove={payload => decide(detail.batch.batch_id, 'approve', payload)}
            onReject={() => decide(detail.batch.batch_id, 'reject')} />}
        </>}

        {tab === 'unmapped' && <>
          <p className="mkt-hint">These lines name a supplier that is not in your vendor list for this organization. Vendors are never matched by name automatically. Add or correct the vendor in <a href="/vendors">Vendors</a>, then re-import.</p>
          <Table headers={['Supplier reference', 'SKU', 'Pack price', 'Per unit', 'Batch']} empty={!unmapped.length} emptyText="No unmapped supplier references." label="Unmapped">
            {unmapped.map(u => <tr key={u.revision_id || `${u.supplier_id}-${u.supplier_sku}`}><td>{u.supplier_id}</td><td>{u.supplier_sku}</td><td className="mkt-number">{packPrice(u)}</td><td className="mkt-number">{perUnit(u)}</td><td>{u.import_batch_id ? <button type="button" disabled={busy} onClick={() => openBatch(u.import_batch_id)}>Open batch</button> : '—'}</td></tr>)}
          </Table>
        </>}
      </>}
    </main>
  );
}
